import { useEffect, useState } from "react";
import type { AdminSessionDetail } from "../../lib/admin-api";

type Props = {
  detail: AdminSessionDetail;
};

export function TranscriptViewer({ detail }: Props) {
  const { session, questions, answers } = detail;
  const answerByQuestionId = new Map(answers.map((a) => [a.questionId, a]));

  const [mdText, setMdText] = useState<string | null>(null);

  useEffect(() => {
    if (!session.finalMdBlobUrl) return;
    void fetch(session.finalMdBlobUrl)
      .then((r) => (r.ok ? r.text() : null))
      .then(setMdText);
  }, [session.finalMdBlobUrl]);

  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <h1 className="font-display text-3xl text-ink-900">{session.subjectName}</h1>
        <div className="flex items-center gap-3 text-sm text-ink-500">
          <span>{session.subjectEmail}</span>
          <span>·</span>
          <span>{humanizeType(session.interviewType)}</span>
          <span>·</span>
          <StatusBadge status={session.status} />
        </div>
        <p className="text-ink-700 italic max-w-2xl">{session.topicContext}</p>
      </header>

      <section className="space-y-6">
        {questions.map((q) => {
          const a = answerByQuestionId.get(q.id);
          return (
            <article key={q.id} className="space-y-3">
              <h2 className="font-display text-lg text-ink-900">
                Q{q.orderIndex + 1}. {q.text}
              </h2>
              {a ? (
                <div className="space-y-2">
                  <p className="text-ink-700 leading-relaxed">{a.transcriptText}</p>
                  <div className="flex items-center gap-4 text-xs text-ink-300">
                    {a.typed && <span>(typed)</span>}
                    {!a.typed && a.audioBlobUrl && (
                      <audio controls src={a.audioBlobUrl} className="h-7" />
                    )}
                    {a.languageMismatch && (
                      <span className="text-warning-600">
                        language mismatch: detected {a.languageDetected}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-ink-300 text-sm">(no answer recorded)</p>
              )}
            </article>
          );
        })}
      </section>

      {mdText && (
        <details className="mt-12 text-sm">
          <summary className="cursor-pointer text-ink-500 hover:text-ink-700 transition-colors">
            View raw markdown ({mdText.length.toLocaleString()} chars)
          </summary>
          <pre className="mt-4 p-4 bg-parchment-100 rounded-lg overflow-x-auto text-xs whitespace-pre-wrap font-mono">
            {mdText}
          </pre>
        </details>
      )}
    </div>
  );
}

function humanizeType(t: string): string {
  return (
    {
      small_business_discovery: "Small-business discovery",
      expertise_capture: "Expertise capture",
      custom: "Custom",
    } as Record<string, string>
  )[t] ?? t;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-parchment-200 text-ink-700">
      {status}
    </span>
  );
}
